import React, { useState, useEffect, useCallback } from 'react';
import {
  ShieldCheck, RefreshCw, Loader2, AlertTriangle, CheckCircle,
  RotateCcw, ChevronDown, ChevronUp, Database, BarChart3,
  Play, XCircle, Clock
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import './DataIntegrity.css';

const SCORE_COLORS = {
  8: '#22c55e', 7: '#4ade80', 6: '#a3e635', 5: '#facc15',
  4: '#fb923c', 3: '#f87171', 2: '#ef4444', 1: '#dc2626', 0: '#991b1b',
};

function DataIntegrity({ departmentCode = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedDept, setExpandedDept] = useState(null);
  const [rescrapingDept, setRescrapingDept] = useState(null);
  const [queuedDepts, setQueuedDepts] = useState(new Set());
  const [rescoreLoading, setRescoreLoading] = useState(false);
  const [actionResult, setActionResult] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await apiClient.get('/api/coverage/integrity');
      setData(result);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRescrape = async (deptCode) => {
    const key = deptCode || 'all';
    if (queuedDepts.has(key)) return; // Already queued
    setRescrapingDept(key);
    setActionResult(null);
    try {
      const result = await apiClient.post('/api/coverage/rescrape', {
        department_code: deptCode || undefined,
      });
      setQueuedDepts(prev => new Set([...prev, key]));
      setActionResult({ ok: true, message: `Queued ${result.jobsCreated} rescrape jobs for ${result.windows} date ranges` });
      fetchData();
    } catch (err) {
      setActionResult({ ok: false, message: err.message });
    } finally {
      setRescrapingDept(null);
    }
  };

  const handleRescore = async () => {
    setRescoreLoading(true);
    try {
      const result = await apiClient.post('/api/coverage/rescore');
      setActionResult({ ok: true, message: `Rescored ${result.scored} records` });
      fetchData();
    } catch (err) {
      setActionResult({ ok: false, message: err.message });
    } finally {
      setRescoreLoading(false);
    }
  };

  if (loading && !data) {
    return <div className="di-loading"><Loader2 size={16} className="di-spin" /> Loading integrity data...</div>;
  }

  const summary = data?.summary || {};
  const distribution = data?.distribution || [];
  const allDepartments = data?.byDepartment || [];
  // byDepartment rows are keyed by department_id (canonical int PK). If the
  // parent passed an int-ish id, match on that first; otherwise keep the
  // legacy text-code fallbacks so nothing breaks during the migration.
  const departments = departmentCode
    ? allDepartments.filter(d =>
        String(d.department_id) === String(departmentCode) ||
        d.department_code === departmentCode ||
        d.code === departmentCode)
    : allDepartments;
  const totalRecords = parseInt(summary.total || 0);
  const needsRescrape = parseInt(summary.needs_rescrape || 0);
  const perfect = parseInt(summary.perfect || 0);
  const avgScore = parseFloat(summary.avg_score || 0);
  const healthPct = totalRecords > 0 ? Math.round(perfect / totalRecords * 100) : 0;

  return (
    <div className="di-container">
      {/* Header */}
      <div className="di-header">
        <div className="di-header-left">
          <ShieldCheck size={18} />
          <h3>Data Integrity</h3>
          <span className={`di-health-badge ${healthPct >= 80 ? 'di-health-good' : healthPct >= 50 ? 'di-health-warn' : 'di-health-bad'}`}>
            {healthPct}% Complete
          </span>
        </div>
        <div className="di-header-right">
          <button className="di-btn di-btn-ghost" onClick={handleRescore} disabled={rescoreLoading}>
            {rescoreLoading ? <Loader2 size={12} className="di-spin" /> : <RefreshCw size={12} />}
            Rescore
          </button>
          <button className="di-btn di-btn-ghost" onClick={fetchData}>
            <RefreshCw size={12} /> Refresh
          </button>
          <button
            className="di-btn di-btn-primary"
            onClick={() => handleRescrape(null)}
            disabled={rescrapingDept === 'all' || queuedDepts.has('all')}
          >
            {rescrapingDept === 'all' ? <Loader2 size={12} className="di-spin" /> :
             queuedDepts.has('all') ? <><CheckCircle size={12} /> All Queued</> :
             <><Play size={12} /> Rescrape All Gaps</>}
          </button>
        </div>
      </div>

      {/* Action Result */}
      {actionResult && (
        <div className={`di-alert ${actionResult.ok ? 'di-alert-ok' : 'di-alert-err'}`}>
          {actionResult.ok ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}
          {actionResult.message}
          <button className="di-alert-dismiss" onClick={() => setActionResult(null)}><XCircle size={14} /></button>
        </div>
      )}

      {error && (
        <div className="di-alert di-alert-err"><AlertTriangle size={14} /> {error}</div>
      )}

      {/* Summary Cards */}
      <div className="di-summary-row">
        <div className="di-summary-card">
          <Database size={16} />
          <div className="di-summary-value">{totalRecords.toLocaleString()}</div>
          <div className="di-summary-label">Total Records</div>
        </div>
        <div className="di-summary-card di-card-good">
          <CheckCircle size={16} />
          <div className="di-summary-value">{perfect.toLocaleString()}</div>
          <div className="di-summary-label">Fully Complete (8/8)</div>
        </div>
        <div className="di-summary-card di-card-warn">
          <AlertTriangle size={16} />
          <div className="di-summary-value">{needsRescrape.toLocaleString()}</div>
          <div className="di-summary-label">Needs Rescrape</div>
        </div>
        <div className="di-summary-card">
          <BarChart3 size={16} />
          <div className="di-summary-value">{avgScore.toFixed(1)}/8</div>
          <div className="di-summary-label">Avg Completeness</div>
        </div>
      </div>

      {/* Score Distribution Bar */}
      <div className="di-distribution">
        <div className="di-distribution-label">Completeness Distribution</div>
        <div className="di-distribution-bar">
          {distribution.map(d => {
            const score = parseInt(d.completeness_score);
            const count = parseInt(d.count);
            const pct = totalRecords > 0 ? (count / totalRecords * 100) : 0;
            if (pct < 0.5) return null;
            return (
              <div
                key={score}
                className="di-distribution-segment"
                style={{ width: `${pct}%`, background: SCORE_COLORS[score] || '#64748b' }}
                title={`Score ${score}/8: ${count.toLocaleString()} records (${pct.toFixed(1)}%)`}
              >
                {pct > 5 && <span>{score}/8</span>}
              </div>
            );
          })}
        </div>
        <div className="di-distribution-legend">
          {distribution.map(d => (
            <span key={d.completeness_score} className="di-legend-item">
              <span className="di-legend-dot" style={{ background: SCORE_COLORS[parseInt(d.completeness_score)] }} />
              {d.completeness_score}/8: {parseInt(d.count).toLocaleString()}
            </span>
          ))}
        </div>
      </div>

      {/* Department Breakdown */}
      <div className="di-dept-list">
        <div className="di-dept-header-row">
          <span>Department</span>
          <span>Records</span>
          <span>Complete</span>
          <span>Needs Rescrape</span>
          <span>Avg Score</span>
          <span>Action</span>
        </div>
        {departments.map(dept => {
          const total = parseInt(dept.total);
          const rescrapeCount = parseInt(dept.needs_rescrape);
          const complete = parseInt(dept.fully_complete || 0);
          const pct = total > 0 ? Math.round(complete / total * 100) : 0;
          const isExpanded = expandedDept === dept.department_code;

          return (
            <div key={dept.department_code + dept.department_name} className="di-dept-item">
              <div className="di-dept-row" onClick={() => setExpandedDept(isExpanded ? null : dept.department_code)}>
                <span className="di-dept-name">
                  {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  <strong>{dept.department_code || '?'}</strong>
                  {dept.department_name?.substring(0, 30)}
                </span>
                <span className="di-dept-count">{total.toLocaleString()}</span>
                <span className="di-dept-complete">
                  <span className={`di-pct ${pct >= 80 ? 'di-pct-good' : pct >= 50 ? 'di-pct-warn' : 'di-pct-bad'}`}>
                    {pct}%
                  </span>
                </span>
                <span className="di-dept-rescrape">
                  {rescrapeCount > 0 ? (
                    <span className="di-rescrape-count">{rescrapeCount.toLocaleString()}</span>
                  ) : (
                    <CheckCircle size={14} className="di-check" />
                  )}
                </span>
                <span className="di-dept-score">{parseFloat(dept.avg_score).toFixed(1)}/8</span>
                <span className="di-dept-action">
                  {rescrapeCount > 0 && (
                    queuedDepts.has(dept.department_code) ? (
                      <span className="di-queued-badge"><CheckCircle size={12} /> Queued</span>
                    ) : (
                      <button
                        className="di-btn di-btn-sm"
                        onClick={(e) => { e.stopPropagation(); handleRescrape(dept.department_code); }}
                        disabled={rescrapingDept === dept.department_code}
                      >
                        {rescrapingDept === dept.department_code ? (
                          <Loader2 size={12} className="di-spin" />
                        ) : (
                          <RotateCcw size={12} />
                        )}
                        Rescrape
                      </button>
                    )
                  )}
                </span>
              </div>

              {isExpanded && (
                <div className="di-dept-detail">
                  <div className="di-detail-grid">
                    <div className="di-detail-item">
                      <span className="di-detail-label">Fully Complete (8/8)</span>
                      <span className="di-detail-value">{complete.toLocaleString()}</span>
                    </div>
                    <div className="di-detail-item">
                      <span className="di-detail-label">Severely Incomplete (≤4/8)</span>
                      <span className="di-detail-value di-val-bad">{parseInt(dept.severely_incomplete || 0).toLocaleString()}</span>
                    </div>
                    <div className="di-detail-item">
                      <span className="di-detail-label">Needs Rescrape</span>
                      <span className="di-detail-value di-val-warn">{rescrapeCount.toLocaleString()}</span>
                    </div>
                    <div className="di-detail-item">
                      <span className="di-detail-label">Average Score</span>
                      <span className="di-detail-value">{parseFloat(dept.avg_score).toFixed(1)}/8</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default DataIntegrity;
